import { useEffect, useRef, useState } from 'react';
import { useToday } from '../hooks/useToday';
import SlotSuggestionCard from '../components/SlotSuggestionCard';
import DaySelector from '../components/DaySelector';
import { todayKey, startOfWeek, weekDates } from '../lib/dateUtils';
import { inputStyle, inputDangerStyle } from '../lib/theme';
import { useTier } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import Toast from '../components/Toast';

const CALORIE_LIMIT_KEY = 'dishsensei.calorieLimit';
const TOAST_DURATION_MS = 2500;

export default function WeekPlanner() {
  const tier = useTier();
  const today = todayKey();
  const dates = weekDates(startOfWeek(today));
  const [selectedDate, setSelectedDate] = useState(today);
  const [calorieLimit, setCalorieLimit] = useState(() => localStorage.getItem(CALORIE_LIMIT_KEY) ?? '');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimeoutRef = useRef<number>();

  const { data, loading, chooseDish, ateOut, undoLog, refreshSlot } = useToday(selectedDate);

  useEffect(() => {
    localStorage.setItem(CALORIE_LIMIT_KEY, calorieLimit);
  }, [calorieLimit]);

  useEffect(() => () => window.clearTimeout(toastTimeoutRef.current), []);

  function showToast() {
    setToastVisible(true);
    window.clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = window.setTimeout(() => setToastVisible(false), TOAST_DURATION_MS);
  }

  const totalCalories = data?.slots.reduce((sum, s) => sum + (s.logged?.dish?.calories ?? 0), 0) ?? 0;
  const limitNum = Number(calorieLimit);
  const isOverLimit = calorieLimit !== '' && limitNum > 0 && totalCalories > limitNum;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <DaySelector
        dates={dates}
        selectedDate={selectedDate}
        todayDate={today}
        onSelect={setSelectedDate}
        after={
          tier === 'PRO' ? (
            <input
              id="calorie-limit"
              type="number"
              min={0}
              placeholder="Daily kka"
              value={calorieLimit}
              onChange={(e) => setCalorieLimit(e.target.value)}
              style={{ ...(isOverLimit ? inputDangerStyle : inputStyle), width: 100, height: 56, boxSizing: 'border-box' }}
            />
          ) : undefined
        }
      />

      <div>
        {loading || !data ? (
          <Spinner />
        ) : (
          data.slots.map((slotData) => (
            <SlotSuggestionCard
              key={slotData.slot}
              data={slotData}
              onChoose={(dishId, source) => chooseDish(slotData.slot, dishId, source).then(showToast)}
              onAteOut={() => ateOut(slotData.slot)}
              onUndo={undoLog}
              onSwap={(excludeIds) => refreshSlot(slotData.slot, excludeIds)}
            />
          ))
        )}
      </div>

      <Toast message="Nice choice!" visible={toastVisible} />
    </div>
  );
}
